import { useCallback, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useQueryParams,
  NumericArrayParam,
  BooleanParam,
  NumberParam,
  StringParam,
  withDefault,
} from 'use-query-params';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useProjectOrLatestChild, useSuspendInitialLoading } from '@/containers/GroupProject/hooks';
import type { ProjectClips } from '@trinity/chat/types';
import {
  ProjectClipsStarQuoteContext,
  ProjectClipsDataContext,
  ProjectClipsSortContext,
  ProjectClipsPaginationContext,
  ProjectClipsFiltersContext,
  SetProjectClipsFiltersContext,
  ProjectClipsTranscriptSearchContext,
  ProjectClipsUpdateTranscriptRangeTagsContext,
} from './context';
import type { Filters, StarQuote, UpdateTranscriptRangeTags } from './interfaces';

type Props = {
  children: React.ReactNode;
};

export const HighlightsContainer = (props: Props) => {
  const project = useProjectOrLatestChild();

  // const tagsQuery = useContext(ProjectTagsQueryContext);

  const [q, setQuery] = useQueryParams({
    keyword: withDefault(StringParam, ''),
    groupTagIds: NumericArrayParam,
    tagIds: NumericArrayParam,
    speakerRole: withDefault(StringParam, 'all'),
    transcriptIds: NumericArrayParam,
    pageSize: withDefault(NumberParam, 25),
    pageIndex: withDefault(NumberParam, 0),
    sort: withDefault(StringParam, 'tags'),
    sortDesc: withDefault(BooleanParam, false, false),
  });

  const onSortClick = useCallback((sort: string) => {
    setQuery({
      pageIndex: 0,
      sort,
      sortDesc: sort === q.sort ? !q.sortDesc : true,
    }, 'replaceIn');
  }, [setQuery, q.sortDesc, q.sort]);

  const setFilters = useCallback((filters: Partial<Filters>) => {
    setQuery({
      pageIndex: 0,
      ...filters,
    }, 'replaceIn');
  }, [setQuery]);

  // Using query param withDefault doesn't seem to memoize array properly
  const filters = useMemo(() => ({
    keyword: q.keyword,
    groupTagIds: q.groupTagIds || [],
    speakerRole: (q.speakerRole || 'all') as ProjectClips.SpeakerFilter,
    tagIds: q.tagIds || [],
    transcriptIds: q.transcriptIds || [],
  }), [q]);

  const clipsQueryProps = useMemo(() => ({
    groupTagIds: filters.groupTagIds,
    projectId: project.id,
    keywords: filters.keyword ? [filters.keyword] : [],
    pageIndex: q.pageIndex,
    pageSize: q.pageSize,
    sort: q.sort,
    sortDesc: !!q.sortDesc,
    speakerRole: filters.speakerRole,
    tagIds: filters.tagIds,
    transcriptIds: filters.transcriptIds,
  }), [filters, project.id, q.pageIndex, q.pageSize, q.sort, q.sortDesc]);

  const api = useChatApiClient();

  const clipsQuery = useQuery({
    queryKey: ['get:projects/clips', clipsQueryProps],
    queryFn: () => {
      return api.projects.fetchProjectClips(clipsQueryProps);
    },
  });

  const transcriptsQuery = useQuery({
    queryKey: ['get:project/transcripts', project.id],
    queryFn: () => {
      return api.projects.getProjectTranscripts({
        projectId: project.id,
      }).then(res => res.transcripts);
    },
  });

  const loading = useMemo(() => {
    return clipsQuery.isLoading && !clipsQuery.data;
  }, [clipsQuery.isLoading, clipsQuery.data]);

  const data = useMemo(() => {
    return loading
      ? getLazyTableData(25)
      : clipsQuery.data?.items ?? [];
  }, [loading, clipsQuery.data]);

  const empty = useMemo(() => {
    return clipsQuery.isFetchedAfterMount && !loading && !data.length;
  }, [clipsQuery.isFetchedAfterMount, loading, data]);

  const suspended = useSuspendInitialLoading({
    data,
    empty,
    isInitialLoading: clipsQuery.isInitialLoading,
    loading,
    pagination: {
      pageCount: clipsQuery.data ? clipsQuery.data.items.length : 0,
      totalCount: clipsQuery.data ? clipsQuery.data.total : 0,
    },
  }, 500);

  const paginationContext = {
    pageSize: q.pageSize,
    pageIndex: q.pageIndex,
    setPageSize: useCallback((pageSize: number) => setQuery({ pageSize }, 'replaceIn'), [setQuery]),
    setPageIndex: useCallback((pageIndex: number) => setQuery({ pageIndex }, 'replaceIn'), [setQuery]),
    totalRows: suspended.pagination.totalCount,
  };

  const sortContext = {
    sort: q.sort,
    sortDesc: q.sortDesc,
    onSortClick,
  };

  return (
    <ProjectClipsTranscriptSearchContext.Provider value={transcriptsQuery.data || []}>
      <SetProjectClipsFiltersContext.Provider value={setFilters}>
        <ProjectClipsFiltersContext.Provider value={filters}>
          <ProjectClipsPaginationContext.Provider value={paginationContext}>
            <ProjectClipsSortContext.Provider value={sortContext}>
              <ProjectClipsDataContext.Provider value={suspended}>
                {props.children}
              </ProjectClipsDataContext.Provider>
            </ProjectClipsSortContext.Provider>
          </ProjectClipsPaginationContext.Provider>
        </ProjectClipsFiltersContext.Provider>
      </SetProjectClipsFiltersContext.Provider>
    </ProjectClipsTranscriptSearchContext.Provider>
  );
};

HighlightsContainer.displayName = 'Highlights.Container';

const getLazyTableData = <T extends ProjectClips.Item>(pageSize = 25) => {
  return Array.from({ length: pageSize }, _ => ({} as T));
};